<script setup>
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { STATUS } from 'dashboard/store/constants';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  metric: {
    type: Object,
    default: () => ({}),
  },
  accountSummaryKey: {
    type: String,
    default: 'getAccountSummary',
  },
  summaryFetchingKey: {
    type: String,
    default: 'getAccountSummaryFetchingStatus',
  },
});

const { t } = useI18n();

const { calculateTrend, displayMetric, isAverageMetricType, fetchingStatus } =
  useReportMetrics(props.accountSummaryKey, props.summaryFetchingKey);

const trendColor = (value, key) => {
  if (isAverageMetricType(key)) {
    return value > 0
      ? 'border-n-ruby-9 text-n-ruby-9'
      : 'border-n-teal-10 text-n-teal-10';
  }
  return value < 0
    ? 'border-n-ruby-9 text-n-ruby-9'
    : 'border-n-teal-10 text-n-teal-10';
};
</script>

<template>
  <div class="text-muted-foreground flex flex-col gap-1">
    <span class="text-xs font-bold uppercase tracking-widest opacity-80">
      {{ metric.NAME }}
    </span>
    <div class="flex items-end gap-3 text-foreground mt-1">
      <div v-if="fetchingStatus === STATUS.FETCHING" class="py-1">
        <Spinner />
      </div>
      <div
        v-else-if="fetchingStatus === STATUS.FAILED"
        class="text-destructive text-sm font-medium bg-destructive/10 px-2 py-0.5 rounded-lg"
      >
        {{ t('REPORT.SUMMARY_FETCHING_FAILED') }}
      </div>
      <div
        v-else-if="fetchingStatus === STATUS.FINISHED"
        class="text-3xl font-black tracking-tight"
      >
        {{ displayMetric(metric.KEY) }}
      </div>
      <div
        v-if="metric.trend && fetchingStatus === STATUS.FINISHED"
        class="text-xs flex items-center mb-1.5 px-2 py-1 rounded-md border"
        :class="[
          trendColor(metric.trend, metric.KEY).includes('ruby') ||
          trendColor(metric.trend, metric.KEY).includes('destructive')
            ? 'bg-destructive/10 border-destructive/20 text-destructive'
            : 'bg-green-500/10 border-green-500/20 text-green-600',
        ]"
      >
        <span
          class="size-3.5 mr-1"
          :class="
            metric.trend < 0 ? 'i-lucide-trending-down' : 'i-lucide-trending-up'
          "
        />
        <span class="font-bold"> {{ calculateTrend(metric.KEY) }}% </span>
      </div>
    </div>
  </div>
</template>
